// ---------------------------------------------------------------------------
// Prezio Onboard Widget — WidgetConfigContext
// ---------------------------------------------------------------------------
// Provides widget-level display preferences to deeply nested components
// without prop drilling. Read via the `useWidgetConfig()` hook.
// ---------------------------------------------------------------------------

import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { CountryPreset } from '@/utils/configSanitizer';

/** Widget display configuration (non-theme, non-session). */
export interface WidgetConfig {
  /** Whether step subtitles are visible. Defaults to `true`. */
  showStepSubtitles: boolean;
  /** Show geocoding technical details on the confirmation screen. Defaults to `false`. */
  demoMode: boolean;
  /** Per-country predefined step values. Empty object when no presets are configured. */
  countryPresets: Record<string, CountryPreset>;
  /** URL to an upgrade page shown in the sandbox coverage nudge. `null` when not configured. */
  upgradeUrl: string | null;
}

const DEFAULT_CONFIG: WidgetConfig = {
  showStepSubtitles: true,
  demoMode: false,
  countryPresets: {},
  upgradeUrl: null,
};

const WidgetConfigCtx = createContext<WidgetConfig>(DEFAULT_CONFIG);

export interface WidgetConfigProviderProps {
  showStepSubtitles?: boolean;
  demoMode?: boolean;
  countryPresets?: Record<string, CountryPreset>;
  upgradeUrl?: string | null;
  children: ReactNode;
}

export function WidgetConfigProvider({
  showStepSubtitles = true,
  demoMode = false,
  countryPresets = {},
  upgradeUrl = null,
  children,
}: WidgetConfigProviderProps) {
  const value = useMemo<WidgetConfig>(
    () => ({ showStepSubtitles, demoMode, countryPresets, upgradeUrl }),
    [showStepSubtitles, demoMode, countryPresets, upgradeUrl],
  );
  return <WidgetConfigCtx.Provider value={value}>{children}</WidgetConfigCtx.Provider>;
}

/** Read widget display configuration. */
export function useWidgetConfig(): WidgetConfig {
  return useContext(WidgetConfigCtx);
}
